import * as React from 'react';
import {Mutation, Query} from 'react-apollo';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    GetSetting as GetSettingQuery,
    GetSettingVariables as GetSettingQueryVariables,
    UpdateSetting as UpdateSettingMutation,
    UpdateSettingVariables as UpdateSettingMutationVariables,
    Setting as GraphqlSetting,
} from 'nutshell-graphql-types';

import UPDATE_SETTING from './graphql/mutations/update-setting.graphql';
import GET_SETTING from './graphql/queries/get-setting.graphql';

class GetSetting extends Query<GetSettingQuery, GetSettingQueryVariables> {}
class UpdateSetting extends Mutation<UpdateSettingMutation, UpdateSettingMutationVariables> {}

type OwnProps = {
    settingName: string;
    isGlobal?: boolean;
    children: (arg1: {
        setting: GraphqlSetting | null | undefined;
        updateSetting: (arg1: string) => Promise<any>;
        refetch: () => Promise<any>;
        isLoading: boolean;
        isUpdating: boolean;
        error: string | null | undefined;
    }) => React.ReactElement;
    // Set this to true for rare instances where you don't want the
    // page to immediately reflect setting changes. For example, if a setting
    // is going to refresh the page it's nice to not have the UI flicker
    fetchPolicyNoCache?: boolean;
};

export const Setting = (props: OwnProps) => (
    <GetSetting
        query={GET_SETTING}
        variables={{
            settingName: props.settingName,
        }}
        notifyOnNetworkStatusChange={true}
    >
        {({data, refetch, error, loading}) => {
            const setting = data ? data.setting : null;
            // @ts-expect-error
            const errorMessage = safelyGetGraphQLErrorMessage(error);

            return (
                <UpdateSetting
                    mutation={UPDATE_SETTING}
                    fetchPolicy={props.fetchPolicyNoCache ? 'no-cache' : undefined}
                >
                    {(updateSetting, {loading: mutationIsRunning}) => {
                        return props.children({
                            setting: setting,
                            refetch,
                            isLoading: loading,
                            isUpdating: mutationIsRunning,
                            error: errorMessage,
                            updateSetting: (newValue) => {
                                return updateSetting({
                                    variables: {
                                        setting: {
                                            name: props.settingName,
                                            value: newValue,
                                            isGlobal: props.isGlobal,
                                        },
                                    },
                                }).then((mutationResult) => {
                                    refetch();

                                    return mutationResult;
                                });
                            },
                        });
                    }}
                </UpdateSetting>
            );
        }}
    </GetSetting>
);
